import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronUp, FaSlidersH } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Box, Flex, Grid, Link, Typography } from '@tq/ui-components';

import { DateFilter, MultiSelect, SearchText } from 'components';
import { FilterOption } from '../../types';
import { RecoilState } from 'recoil';
import { handleFilterChanged, handleFilterRemoval } from './filterUtils';

type TableFiltersWithDateProps = {
  filters: FilterOption[];
  onSetSelectedFilters: (filterOptions: FilterOption[]) => void;
  textSearchState: RecoilState<string>;
  collapsible?: boolean;
  showDateFilter?: boolean;
  initialStartDate?: Date;
  initialEndDate?: Date;
  onSetStartDate?: (date: Date) => void;
  onSetEndDate?: (date: Date) => void;
  fromDateLabel?: string;
  toDateLabel?: string;
};

export const TableFiltersWithDate: React.FC<TableFiltersWithDateProps> = ({
  filters,
  onSetSelectedFilters,
  textSearchState,
  collapsible,
  showDateFilter,
  initialStartDate,
  initialEndDate,
  onSetStartDate,
  onSetEndDate,
  fromDateLabel = undefined,
  toDateLabel = undefined,
}) => {
  const { t } = useTranslation('monitor');
  const [showFilters, setShowFilters] = useState(!collapsible);

  const handleSelect = (filter: FilterOption | undefined, value: number | string) => {
    handleFilterChanged(filters, filter, value, onSetSelectedFilters);
  };

  const handleRemoveSelection = (filter: FilterOption, value: number | string) => {
    handleFilterRemoval(filters, filter, value, onSetSelectedFilters);
  };

  return (
    <Grid
      gridGap={4}
      border={{ base: 'none', lg: `1px solid ${COLOURS.gray[300]}` }}
      borderRadius={5}
      p={{ base: 0, lg: 4 }}
      data-testid='table-filters'
    >
      {collapsible && (
        <Flex justifyContent='space-between'>
          <Flex alignItems='center' gridGap={2}>
            <FaSlidersH fontSize={24} color={COLOURS.blue[600]} />
            <Typography>
              <strong>{t('filters', 'Filters')}</strong>
            </Typography>
          </Flex>
          <Link onClick={() => setShowFilters((prevShow) => !prevShow)}>
            <strong>
              <Flex gridGap={4} alignItems='center'>
                <Typography fontSize='sm'>
                  {showFilters ? t('hide_filters', 'Hide') : t('show_filters', 'Show')}
                </Typography>
                <Typography>{showFilters ? <FaChevronUp /> : <FaChevronDown />}</Typography>
              </Flex>
            </strong>
          </Link>
        </Flex>
      )}

      {showFilters && (
        <Flex flexWrap='wrap' alignItems='start'>
          <Flex flex='0 0 100%' gridGap='16px 32px' pb={8}>
            <Box
              w={{
                base: 'calc((100% - 64px) / 2)',
                lg: 'calc((100% - 54px) / 3)',
              }}
            >
              <SearchText
                label={t(
                  'search_for_planning_applications_label',
                  'Search for planning applications'
                )}
                placeholderText={t('search_placeholder', 'Search')}
                hideSearchInfo
                searchState={textSearchState}
                onChanged={(text) =>
                  handleSelect(
                    filters.find((x) => x.isSearchText),
                    text
                  )
                }
              />
            </Box>
            {showDateFilter && onSetStartDate && onSetEndDate && (
              <DateFilter
                initialEndDate={initialEndDate}
                initialStartDate={initialStartDate}
                onSetEndDate={onSetEndDate}
                onSetStartDate={onSetStartDate}
                fromDateLabel={fromDateLabel}
                toDateLabel={toDateLabel}
              />
            )}
          </Flex>
          <Flex flex='0 0 100%' gridGap='16px 32px' alignItems='start'>
            {filters
              .filter((x) => !x.isSearchText)
              .map((filter: FilterOption) => (
                <MultiSelect
                  w={{
                    base: '100%',
                    lg: 'calc((100% - 64px) / 3)',
                  }}
                  allowMultiSelect={filter.allowMultiSelect}
                  key={filter.name}
                  label={filter.header}
                  items={filter.items}
                  selectedItems={filter.selectedItems}
                  handleSelect={(value) => handleSelect(filter, value)}
                  handleRemoveSelection={(value) => handleRemoveSelection(filter, value)}
                  fieldId={filter.name.replace(/\s/g, '-')}
                />
              ))}
          </Flex>
        </Flex>
      )}
    </Grid>
  );
};
